import { Component, HostListener, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { environment } from 'environments/environment';
import { AuthService } from './auth/auth.service';
import { BnNgIdleService } from 'bn-ng-idle';
import { Meta, Title } from '@angular/platform-browser';
import { filter, map } from 'rxjs';
import { GlobalToasterService } from './services/global-toaster.service';
import { IdaMessageBridgeService } from './services/ida-message-bridge.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
    private readonly idaAllowedOrigins: Set<string> =
        this.buildIdaAllowedOrigins();
    private readonly idaMessageHandler = (event: MessageEvent): void => {
        this.handleIdaMessage(event);
    };

    constructor(
        private bnIdle: BnNgIdleService,
        private translate: TranslateService,
        private router: Router,
        private gaService: GoogleAnalyticsService,
        private authService: AuthService,
        private titleService: Title,
        public globalToasterSerive: GlobalToasterService,
        private metaService: Meta,
        private idaMessageBridgeService: IdaMessageBridgeService,
    ) {
        /* Remove chatbot language if its there and only
        will set it back if click on language change */
        if (localStorage.getItem('chatbotLanguage')) {
            localStorage.removeItem('chatbotLanguage');
        }

        /* Remove event title if its there and only
        will set it back if click on Register button */
        if (localStorage.getItem('event_title')) {
            localStorage.removeItem('event_title');
        }

        /* Set Popup Modal for Chatbot by default */
        localStorage.setItem('modal', 'false');

        /* Set language by default */
        let language: any;
        if (localStorage.getItem('switched_language')) {
            language = localStorage.getItem('switched_language');
            localStorage.setItem('language', language);
        } else {
            localStorage.setItem('language', 'bm');
            language = localStorage.getItem('language');
        }
        this.translate.use(language);
        if (!this.authService.isLoggedIn()) {
            // this.router.navigate(['/login']);
            // Redirect to the login page or perform other actions
        }
        this.bnIdle
            .startWatching(environment.Ideal_Timeout)
            .subscribe((isTimedOut: boolean) => {
                if (isTimedOut) {
                    if (this.authService.isLoggedIn()) {
                        this.authService.signOut();
                    }
                }
            });
    }

    ngOnInit() {
        window.addEventListener('message', this.idaMessageHandler);
        this.setPageTitle();
    }

    ngOnDestroy(): void {
        window.removeEventListener('message', this.idaMessageHandler);
    }

    private buildIdaAllowedOrigins(): Set<string> {
        const allowedOrigins = new Set<string>();
        allowedOrigins.add(window.location.origin);

        const chatbotOrigin = this.getOriginFromUrl(environment.CHATBOT_URL);
        if (chatbotOrigin) {
            allowedOrigins.add(chatbotOrigin);
        }

        allowedOrigins.add('https://static.uat.pave.com.my');
        return allowedOrigins;
    }

    private getOriginFromUrl(url: string): string | null {
        try {
            return new URL(url).origin;
        } catch {
            return null;
        }
    }

    private logIdaDebug(message: string, data?: unknown): void {
        if (!environment.production) {
            if (typeof data !== 'undefined') {
                console.log(`[iDA DEBUG] ${message}`, data);
            } else {
                console.log(`[iDA DEBUG] ${message}`);
            }
        }
    }

    private handleIdaMessage(event: MessageEvent): void {
        if (!this.idaAllowedOrigins.has(event.origin)) {
            this.logIdaDebug('Rejected message due to origin mismatch', {
                incomingOrigin: event.origin,
                allowedOrigins: Array.from(this.idaAllowedOrigins),
            });
            return;
        }

        if (!event.data || typeof event.data !== 'object') {
            this.logIdaDebug('Rejected message because payload is not object', {
                payload: event.data,
            });
            return;
        }

        const payload = event.data as Record<string, unknown>;

        if (payload.type === 'IDA_USER_INPUT') {
            this.idaMessageBridgeService.publishUserInput({
                value: payload.value,
                field:
                    typeof payload.field === 'string'
                        ? payload.field
                        : undefined,
                raw: payload,
            });
            return;
        }

        if (payload.type === 'IDA_BOT_RESPONSE') {
            this.idaMessageBridgeService.publishBotResponse({
                message: payload.message,
                raw: payload,
            });
            return;
        }

        if (payload.type === 'CLOSE_CHATBOT') {
            const messengerDiv = document.querySelector('.messenger-wrapper');
            const chatIcon = document.querySelector('.chat-icon');
            const widgetIcon = document.getElementById('widgetIcon');
            const closeIcon = document.getElementById('closeIcon');
            const chatWrapper = document.querySelector('.chat-wrapper');
            if (messengerDiv && messengerDiv.classList.contains('expanded')) {
                messengerDiv.classList.remove('expanded');
                if (chatIcon) chatIcon.classList.remove('hidden');
                if (closeIcon) closeIcon.classList.remove('rotate-fade');
                if (chatWrapper) chatWrapper.setAttribute('opened', 'false');
                if (widgetIcon) widgetIcon.style.background = '#4B6EEC';
            }
            this.logIdaDebug('Chatbot closed via iframe request');
            return;
        }

        if (payload.type === 'IDA_APPLICATION_DATA') {
            const data = payload.data as string;
            if (data) {
                localStorage.setItem('ida-application-data', data);
                localStorage.setItem(
                    'ida-application-data-timestamp',
                    Date.now().toString(),
                );
            }
            this.logIdaDebug('iDA application data stored', data);
            return;
        }

        if (payload.type === 'IDA_CLEAR_APPLICATION_DATA') {
            localStorage.removeItem('ida-application-data');
            localStorage.removeItem('ida-application-data-timestamp');
            localStorage.removeItem('ida-business-info-data');
            localStorage.removeItem('ida-business-info-data-timestamp');
            this.logIdaDebug('iDA application data cleared by iframe request');
            return;
        }

    }

    @HostListener('window:storage', ['$event'])
    onStorageEvent(event: StorageEvent): void {
        if (event.key === 'logout-event') {
            // Handle logout event from other tabs
            // this.authService.signOut();
        }
    }

    setPageTitle() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => {
                    let route: ActivatedRoute = this.router.routerState.root;
                    // let routeTitle = '';
                    while (route!.firstChild) {
                        route = route.firstChild;
                    }
                    // if (route.snapshot.data['title']) {
                    //   routeTitle = route!.snapshot.data['title'];
                    // }
                    return route;
                }),
            )
            .subscribe((event: any) => {
                if (!event.data._value.title) {
                    this.titleService.setTitle(null);
                }
                if (event.data._value.title) {
                    this.translate
                        .get(event.data._value.title)
                        .subscribe((translatedTitle) => {
                            this.titleService.setTitle(`${translatedTitle}`);
                        });
                    this.translate
                        .get(event.data._value.keywords)
                        .subscribe((translatedKeywords) => {
                            this.metaService.updateTag({
                                name: 'keywords',
                                content: translatedKeywords,
                            });
                        });
                    this.translate
                        .get(event.data._value.description)
                        .subscribe((translatedDescription) => {
                            this.metaService.updateTag({
                                name: 'description',
                                content: translatedDescription,
                            });
                        });
                } else {
                    const keywords = this.metaService.getTag('name=keywords');
                    this.metaService.removeTagElement(keywords);
                    const description =
                        this.metaService.getTag('name=description');
                    this.metaService.removeTagElement(description);
                }
            });
    }

    GATagPageView() {
        if (this.router.url == '/home') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Home_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/business-financing') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Products_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/check-eligibilty') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Check_Eligibilty_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/status') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Status_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/login') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Login_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/sign-up') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Signup_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/resend-code') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Resend_code_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/faq') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'FAQ_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/about-us') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'About_Us_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/sign-up-status') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Sign Up Success Page',
                send_to: environment.GA_ID,
                page_path: this.router.url,
            });
        } else if (this.router.url == '/campaign') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Campaign_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/sme') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'MSME_Support_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/insurance') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Insurance_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/cgc-developmental-programme') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Dev_Programme_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/myknp') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'MYKNP_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/esg') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'ESG_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/matched-products') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Matched_Products_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/corporate') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Financial_Info_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/business-info') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Business_Info_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/grants/miti') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Grants_MITI_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/digital-guarantee') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Digital_Guarantee_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/promotions') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Promotion_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/application-document-upload') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Upload_Document_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/contact-us') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Contact_Us_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/partnership') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Partnership_Page',
                send_to: environment.GA_ID,
            });
        } else if (this.router.url == '/digital-services') {
            this.gaService.gtag('event', 'page_view', {
                page_title: 'Digital_Services_Page',
                send_to: environment.GA_ID,
            });
        }
    }

    readLocalStorageValue(key) {
        let value = localStorage.getItem(key);
        return value;
    }
}
